import { useEffect, useState } from 'react'
import TopNavigation from '@cloudscape-design/components/top-navigation'
import type { TopNavigationProps } from '@cloudscape-design/components/top-navigation'
import { applyMode, Mode } from '@cloudscape-design/global-styles'
import { useAuthStore } from '@/state/authStore'
import { logout } from '@/clients/firebase'

const TOP_NAV_CSS = `
  /* Nav bar only — scoped to #h to avoid polluting dropdown portals */
  #h {
    background: rgba(9,9,11,0.85) !important;
    backdrop-filter: blur(20px) !important;
    -webkit-backdrop-filter: blur(20px) !important;
    border-bottom: 1px solid rgba(20,184,166,0.1) !important;
    box-shadow: none !important;
  }
  /* Title / identity */
  #h [class*="identity"],
  #h [class*="identity"] a,
  #h [class*="title"] {
    color: #F0FDF4 !important;
    font-weight: 700 !important;
    letter-spacing: -0.01em !important;
  }
  /* Utility buttons */
  #h button,
  #h [class*="utility-button"],
  #h [class*="utility"] span {
    color: #a1a1aa !important;
  }
  #h button:hover,
  #h [class*="utility-button"]:hover {
    background: rgba(255,255,255,0.06) !important;
    color: #14B8A6 !important;
  }
  /* Dropdown portal — floats outside #h, target via context class */
  [class*="awsui-context-top-navigation"] [class*="dropdown"],
  [class*="awsui-context-top-navigation"] [class*="list"] {
    background: #111113 !important;
    border: 1px solid #1e1e22 !important;
  }
  [class*="awsui-context-top-navigation"] [class*="item"],
  [class*="awsui-context-top-navigation"] [class*="option"] {
    color: #a1a1aa !important;
  }
  [class*="awsui-context-top-navigation"] [class*="item"]:hover,
  [class*="awsui-context-top-navigation"] [class*="option"]:hover {
    background: rgba(16,185,129,0.08) !important;
    color: #F0FDF4 !important;
  }
  /* Remove Cloudscape focus ring on dropdown items */
  [class*="awsui-context-top-navigation"] [class*="item"]:focus,
  [class*="awsui-context-top-navigation"] [class*="option"]:focus,
  [class*="awsui-context-top-navigation"] button:focus,
  [class*="awsui-context-top-navigation"] a:focus {
    box-shadow: none !important;
    outline: none !important;
  }
`

export default function TopNav() {
  const account = useAuthStore((s) => s.account)
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    const style = document.createElement('style')
    style.id = 'top-nav-brand-theme'
    style.textContent = TOP_NAV_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  const handleSignOut = async () => {
    await logout()
  }

  const handleDarkModeToggle = () => {
    const next = !isDark
    setIsDark(next)
    applyMode(next ? Mode.Dark : Mode.Light)
  }

  const utilities: TopNavigationProps['utilities'] = [
    {
      type: 'button',
      text: isDark ? 'Light mode' : 'Dark mode',
      onClick: handleDarkModeToggle,
    },
    {
      type: 'menu-dropdown',
      text: account?.display_name ?? account?.email ?? 'Account',
      iconName: 'user-profile',
      items: [{ id: 'signout', text: 'Sign out' }],
      onItemClick: ({ detail }) => {
        if (detail.id === 'signout') {
          void handleSignOut()
        }
      },
    },
  ]

  return (
    <TopNavigation
      identity={{
        title: 'Sabar',
        href: '/',
        logo: { src: '/logo-icon.svg', alt: 'Sabar' },
      }}
      utilities={utilities}
    />
  )
}
